import React, {useState} from 'react'
import {DataGrid, GridColDef, GridRenderCellParams} from '@mui/x-data-grid'
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Link,
    List,
    ListItem,
    ListItemText,
    Tooltip,
    Typography
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {TransferOrderResult} from '../transferUtils'

interface ReceiveDashboardTableProps {
    transferOrders: TransferOrderResult[]
    getModelNumber: (sku: string) => string | null
    onReceive?: (id: string, type?: string) => void
    showActions?: boolean
    handToHand?: boolean
    onSelectId?: (id: string) => void
    received?: boolean,
    highlightedItems?: string[],
    resumeOrder: string | null
}

function ReceiveDashboardTable({
                                   transferOrders,
                                   getModelNumber,
                                   onReceive,
                                   showActions = false,
                                   handToHand = false,
                                   onSelectId,
                                   received,
                                   highlightedItems,
                                   resumeOrder
                               }: ReceiveDashboardTableProps) {
    const [openDialog, setOpenDialog] = useState(false)
    const [selectedItems, setSelectedItems] = useState<any[]>([])

    const columns: GridColDef[] = [
        ...(handToHand
            ? [{
                field: 'transferRequestId',
                headerName: 'Transfer Request ID',
                renderCell: (params: GridRenderCellParams) => (
                    <Box display="flex" justifyContent="flex-start" alignItems="center" marginTop={1.5}>
                        <Typography>
                            {params.value}
                        </Typography>
                    </Box>
                ),
                minWidth: 200
            }]
            : [{
                field: 'tracking_number',
                headerName: 'Tracking Number',
                minWidth: 200,
                renderCell: (params: GridRenderCellParams) => (
                    <Link href={`https://www.fedex.com/fedextrack/?trknbr=${params.value}`} target="_blank"
                          underline="hover">
                        {params.value}
                    </Link>
                )
            }]),
        {
            field: 'status',
            headerName: 'Status',
            minWidth: 200,
            renderCell: (params: GridRenderCellParams) => {
                if (params.value === 'error') {
                    return (
                        <Box display="flex" justifyContent="flex-start" alignItems="center" marginTop={1.5}>
                            <Tooltip title={params.row.message || ''}>
                                <Typography variant="body1" color="error">{params.value}</Typography>
                            </Tooltip>
                        </Box>
                    )
                }
                return (
                    <Box display="flex" justifyContent="flex-start" alignItems="center" marginTop={1.5}>
                        <Typography variant="body1">{params.value}</Typography>
                    </Box>
                )
            }
        },
        {
            field: 'total_qty',
            headerName: received ? 'Total Qty Received' : 'Total Qty Expected',
            minWidth: 150,
            flex: 0.5,
            renderCell: (params: GridRenderCellParams) => (
                <Box display="flex" justifyContent="flex-start" alignItems="center" marginTop={1.5}>
                    <Typography variant="body1" sx={{marginRight: 0.5}}>{params.value}</Typography>
                    <InfoOutlinedIcon fontSize="small" onClick={() => handleQtyClick(params.row.items)} color="primary"
                                      style={{cursor: 'pointer'}} />
                </Box>
            )
        },
        ...(showActions
            ? [{
                field: 'action',
                headerName: 'Action',
                minWidth: 150,
                renderCell: (params: GridRenderCellParams) => (
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={Boolean(resumeOrder && resumeOrder !== params.row.id)}
                        onClick={() => {
                            if (params.row.status !== 'received') {
                                if (onSelectId) onSelectId(params.value)
                                if (onReceive) onReceive(params.row.id, 'h2h')
                            }
                        }}
                    >
                        Receive
                    </Button>
                )
            }]
            : [])
    ]

    const getStatus = (order: TransferOrderResult) => {
        if (handToHand) {
            return order.status
        } else if (order.tracking) {
            return order.tracking.status
        } else {
            return order.status
        }
    }
    //console.log(transferOrders)
    const rows = transferOrders.map((order) => ({
        id: order.transferOrderId,
        transferRequestId: order.transferRequestId,
        tracking_number: order.tracking ? order.tracking.number : 'N/A',
        status: received ? order.status : getStatus(order),
        total_qty: received ? order.receivedItems.reduce((acc, item) => acc + item.quantity, 0) : order.items.reduce((acc, item) => acc + item.quantity, 0),
        items: received ? order.receivedItems : order.items,
        message: order.tracking ? order.tracking.message : '',
        isHighlighted: highlightedItems?.includes(order.transferOrderId.toString())
    }))


    const handleQtyClick = (items: any[]) => {
        setSelectedItems(items)
        setOpenDialog(true)
    }

    const handleCloseDialog = () => {
        setOpenDialog(false)
        setSelectedItems([])
    }

    return (
        <div style={{height: 400, width: '100%', backgroundColor: 'whitesmoke', overflow: 'auto'}}>
            <DataGrid
                rows={rows}
                columns={columns}
                disableRowSelectionOnClick
                getRowClassName={(params) => params.row.isHighlighted ? 'highlighted-row' : ''}
                sx={{'& .highlighted-row': {backgroundColor: 'yellow !important'}}}
            />
            <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth={true} maxWidth={'sm'}>
                <DialogTitle>{received ? 'Received Items' : 'Expected Items'}</DialogTitle>
                <DialogContent>
                    <List>
                        {selectedItems.length > 0 ? (
                            selectedItems.map((item, index) => (
                                <React.Fragment key={index}>
                                    <ListItem>
                                        <ListItemText
                                            primary={`Item: ${item.sku}`}
                                            secondary={
                                                <div>
                                                    <Typography variant="body2">Model
                                                        Number: {getModelNumber(item.sku)}</Typography>
                                                    <Typography variant="body2">Quantity: {item.quantity}</Typography>
                                                </div>
                                            }
                                        />
                                    </ListItem>
                                    {index < selectedItems.length - 1 && <Divider component="li" />}
                                </React.Fragment>
                            ))
                        ) : (
                            <ListItem>
                                <ListItemText primary="No items to display." />
                            </ListItem>
                        )}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} color="primary">Close</Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}

export default ReceiveDashboardTable
